import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from "react-redux";

import ClearFilterBtn from "../../../application/ClearFilterBtn";

import Highcharts from 'highcharts/highstock';

import useDataSource from "../../../../hooks/useDataSource";
import useMainCanvasSize from "../../../../hooks/useMainCanvasSize";
import { filterUpdateConditionAction } from "../../../../redux/data/dataActions";

import {
    getCustomMatchDataColor,
    getDataColor
} from "../../../../utilities/ColorUtilities";

import {
    areLoadDependenciesMet,
    getFieldAlias,
    getFieldFormatters
} from "../../../../utilities/DataSourceUtilities";

import {
    formatDate,
    formatNumber, formatStyle
} from "../../../../utilities/FormatUtilities";

import {
    dataAddFilter,
    dataRemoveFilter
} from "../../../../redux/data/dataActions";
import * as DataUtilities from "../../../../utilities/DataUtilities";
import useWindowSize from "../../../../hooks/useWindowSize";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";

import { styled, Card, CardHeader, CardContent, LinearProgress } from '@mui/material';

const PREFIX = 'PieChart';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none',
    }
}));

export default function PieChart({id, configuration, xId}) {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // App State
    const config = useSelector(state => state.config);
    const filters = useSelector(state => state.data.filters);
    const persist = useSelector(state => state.persist);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);
    const [chart, setChart] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [sliceValue, setSliceValue] = useState(-2112);
    const [sliceValueNotFound, setSliceValueNotFound] = useState(false);
    const [noDataMessage, setNoDataMessage] = useState("No Data");
    // const [categoryFieldAlias, setCategoryFieldAlias] = useState(null);
    const [categoryFieldFormatters, setCategoryFieldFormatters] = useState(null);
    const [countFieldAlias, setCountFieldAlias] = useState(null);
    const [countFieldFormatters, setCountFieldFormatters] = useState(null);
    const [valueFieldAlias, setValueFieldAlias] = useState(null);
    const [valueFieldFormatters, setValueFieldFormatters] = useState(null);
    const [showProgress, setShowProgress] = useState(compConfig.element?.showFetching !== undefined ? compConfig.element?.showFetching : true);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [resizedComponent, setResizedComponent] = useState(false);

	const highchartOptions = compConfig?.highchartOptions ? compConfig?.highchartOptions : {};
	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const dependencies = compConfig.dataSource?.dependencies ? compConfig.dataSource?.dependencies : null;
	const categoryField = compConfig.dataSource?.fields?.categoryField ? compConfig.dataSource?.fields?.categoryField : '';
	const valueField = compConfig.dataSource?.fields?.valueField ? compConfig.dataSource?.fields?.valueField : '';
	const condition = compConfig.dataSource?.condition ? compConfig.dataSource?.condition : null;
	const customColors = compConfig?.element?.colors;
	const styles = compConfig?.element?.styles;
	const filterField = compConfig.xFilter?.field ? compConfig.xFilter?.field : null;
	const filterByValue = compConfig.xFilter?.byValue !== undefined ? compConfig.xFilter?.byValue : false;
	const filterOperator = compConfig.xFilter?.operator ? compConfig.xFilter?.operator : null;

	const chartRef = useRef(null);
	const dataSource = useDataSource(dataSourceName);
	const size = useMainCanvasSize();
	const windowSize = useWindowSize();

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
				setResizedComponent(true);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Pie Chart Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'PieChart'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    useEffect(() => {

        // console.log('-----------------PIE CHART---------STATUS->');
        // console.log('fetching: {0}'.format(dataSource.fetching));
        // console.log('fetched: {0}'.format(dataSource.fetched));
        // console.log('error: {0}'.format(dataSource.error));

        if (dataSource.error) {
            setNoDataMessage("Error");
            setChartData([]);
        }
        else if (dataSource.data && dataSource.fetched) {

            // If our dependencies are not met then we're
            // loading/no chance until they are
            if (!areLoadDependenciesMet(dependencies, filters)) {
                return;
            }

            setNoDataMessage("No Data");

            // Setup base data as filtered results or a baseline if included
            let data =[];
            if (condition) {
                let conditionValue;
                switch (condition.conditionType.toLowerCase()) {
                    case 'group':
                        conditionValue = condition.conditionValue;
                        break;
                    default:
                        conditionValue = condition.conditionValue;
                        break;
                }
                const newData = DataUtilities.filterDataSet(condition.conditionType, condition.conditionOperator, condition.conditionField, conditionValue, dataSource.data, null);
                // If condition found, push, otherwise, keep null/empty
                if (newData !== undefined) {
                    data = newData;
                }
            } else {
                data = dataSource.data;
            }
            if (dataSource.includeBaselineResults && dataSource.baselines.length > 0) {
                // Find my baseline
                const baseline = dataSource.baselines.find(d => {
                    return d.id === id
                });

                // console.log('------------BASELINE------------->', baseline);
                if (baseline) {
                    let useBaseline = true;

                    // If there is a baseline then we use
                    // that value IF we can highlight
                    if (sliceValue !== -2112) {

                        if (!baseline.result.find(d => {
                                    if (d[categoryField] && sliceValue) {
                                        return d[categoryField].toString().toLowerCase() === sliceValue.toString().toLowerCase();
                                    } else {
                                        return false;
                                    }
                                }
                            )
                        ) {
                            useBaseline = false;
                        }
                    }

                    // console.log('------------------USE BASELINE---------------->', useBaseline);
                    if (useBaseline) {
                        data = baseline.result;
                    }
                }
            }

            // Rehydrate slice
            const componentFilters = filters.find(f => {
                return f.id === id && f.field === categoryField
            });

            if (componentFilters) {
                // TODO: Add utility or store in state since this
                // only assumes single condition - for now we'll use
                // local context however sharing across components
                // TBD will share same f.id
                // HACK:
                let condition = componentFilters.conditions[0];
                if (condition.indexOf("eq '") === 0) {
                    // String eq
                    condition = condition.substr(condition.indexOf("eq '") + 4);
                    condition = condition.substr(0, condition.length - 1);

                    setSliceValue(condition);
                } else if (condition.indexOf("eq ") === 0) {
                    // Numeric eq
                    condition = condition.substr(condition.indexOf("eq ") + 3);

                    setSliceValue(condition);
                } else if (condition.indexOf("like '") === 0) {
                    // String like
                    condition = condition.substr(condition.indexOf("like '") + 6);
                    condition = condition.substr(0, condition.length - 1);

                    if (condition.length > 2) {
                        if (condition.substr(0, 1) === '%') {
                            condition = condition.substr(1);
                        }
                        if (condition.substr(condition.length -1) === '%') {
                            condition = condition.substr(0, condition.length -1);
                        }
                    }

                    setSliceValue(condition);
                } else {
                    console.log("**WARN** PieChart '{0}' was not a recognized filter condition".format(condition));
                }
            }

            // Aliases and formatters - using for tooltips
            // setCategoryFieldAlias(getFieldAlias(dataSource, categoryField));
            setCategoryFieldFormatters(getFieldFormatters(dataSource, categoryField));
            setCountFieldAlias(getFieldAlias(dataSource, 'count'));
            setCountFieldFormatters(getFieldFormatters(dataSource, 'count'));
            setValueFieldAlias(getFieldAlias(dataSource, 'value'));
            setValueFieldFormatters(getFieldFormatters(dataSource, 'value'));

            // Update Data/Chart
            setChartData(data);
        }
    }, [dataSource.data]);

    useEffect(() => {

        updateChart();

    }, [chartData, sliceValue, persist.themeLight, t]);

    useEffect(() => {
        const componentFilters = filters.find(f => {
            return f.id === id && f.field === categoryField
        });

        if (!componentFilters && sliceValue !== -2112) {
            setSliceValue(-2112);
        }
    }, [filters]);

    useEffect( () => {
        if (chart) {
            // setTimeout required to accurately obtain chart.renderTo.clientHeight and chart.renderTo.clientWidth to perform the reflow
            setTimeout(function() {
                // console.log('reflow');
                chart.reflow();
				setResizedComponent(resizedComponent === true ? false : false);
			}, 300)
		}
	}, [size, windowSize, resizedComponent, cardHeaderStyle, cardContentStyle])

    const getSeriesColor = (index, value, sliceValueToCompare) => {
        // Example ramping values
        if (sliceValueToCompare === null || sliceValueToCompare == -2112) {
            // There are no selected values so we just want to ramp the colors
            if (customColors !== undefined) {
                return getCustomMatchDataColor(value, customColors);
            } else {
                return getDataColor(index, value, config.data.colorMap);
            }
        } else {
            // We have selected values so we want not selected or the ramp color
            if (value === sliceValueToCompare) {
                if (customColors !== undefined) {
                    return getCustomMatchDataColor(value, customColors);
                } else {
                    return getDataColor(index, value, config.data.colorMap);
                }
            } else {
                return persist.themeLight ? '#767676' : '#c0c2c9';
            }
        }
    }

    const formatTooltip = (formatters, label, input) => {

        // console.log('{0}'.format('--> PieChart: formatTooltip()'));
        // console.log('FORMATTERS: ', formatters);
        // console.log('LABEL: {0} ({1})'.format(label, typeof label));
        // console.log('INPUT: {0} ({1})'.format(input, typeof input));

        let formattedValue, style = "", tooltip;
        if (formatters) {
            for (const formatter of formatters) {
                if (formatter.type.toLowerCase() === 'style') {
                    style = formatStyle(input, formatter.config, input, formatter.condition, true);
                } else if (formatter.type.toLowerCase() === 'number') {
                    formattedValue = formatNumber(input, formatter.config);
                } else if (formatter.type.toLowerCase() === 'date') {
                    formattedValue = formatDate(input, formatter.config);
                }
            }
            if (label) {
                tooltip = "<tr><td style='text-align:left'>" + label + ":</td><td style='text-align:right'><span style=" + style + ">" + formattedValue + "</td></tr>";
            } else {
                tooltip = "<tr><td colspan='2' style=" + style + ">" + input + "</td></tr>";
            }
        }

        // console.log('TOOLTIP: {0} ({1})'.format(tooltip, typeof tooltip));
        return tooltip;
    }

    const clickEvent = (event) => {
        // NOTE: You can look up other values (assuming you have an ID) by
        // filtering the input array

        let filterFieldToAdd;
        let filterValueToAdd;

        if (!filterByValue) {
            filterFieldToAdd = categoryField;
            filterValueToAdd = (typeof event.point !== 'undefined') ? event.point.options.name : event.target.series.name;
        } else {
            filterFieldToAdd = filterField;
            filterValueToAdd = (typeof event.point !== 'undefined') ? event.point.options.y : event.target.series.y;
        }

        if (sliceValue.toString().toLowerCase() === filterValueToAdd.toString().toLowerCase()) {
            dispatch(dataRemoveFilter(id));
        }
        else {
            setSliceValue(filterValueToAdd);
            if (filterOperator.toLowerCase() === 'like') {
                dispatch(dataAddFilter(id, filterFieldToAdd, " like '%{0}%'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
            } else {
                dispatch(dataAddFilter(id, filterFieldToAdd, " eq '{0}'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
            }
        }
    }

    const updateChart = () => {

        const data = chartData;

        if (!chart && data === null)
            return;

        if (chart && data === null) {
            // No Data
            chart.update({
                series: [{
                    name: '{0}'.format(categoryField),
                    data: [],
                    innerSize: highchartOptions.innerSize
                }]
            }, true, true);
            chart.showNoData();
            return;
        }

        const sliceValueToCompare = sliceValue.toString().toLowerCase();

        // Setup series
        const seriesData = [];
        let sliceFound = false;
        for (let d = 0; d < data.length; d++) {
            const item = data[d];

            let value = item[categoryField];
            if (!value)
                value = "";
            else
                value = value.toString().toLowerCase();

            if (value === sliceValueToCompare) {
                sliceFound = true;
            }

            seriesData.push({
                name: item[categoryField],
                y: item[valueField],
                value: item.value,
                // color: getSeriesColor(d, value, sliceValueToCompare),
                color: customColors !== undefined ? (sliceValueToCompare === '-2112' ? getCustomMatchDataColor(value, customColors) : getSeriesColor(d, value, sliceValueToCompare.toLowerCase())) : getSeriesColor(d, value, sliceValueToCompare.toLowerCase()),
                sliced: value === sliceValueToCompare,
                borderWidth:  value === sliceValueToCompare ? config.data.highlight.borderWidth : 0,
                borderColor: value === sliceValueToCompare ? config.data.highlight.borderColor : 0
            });
        }
        // console.log(seriesData);

        // NOTE: If we don't find the active slice what do we want to do??
        // HACK: TEMP: Add the missing slice??
        if (!sliceFound && sliceValueToCompare !== '-2112') {
            seriesData.push({
                name: sliceValue.toUpperCase(),
                y: 0,
                value: 0,
                color: 'red',
                sliced: true,
                borderWidth:  config.data.highlight.borderWidth,
                borderColor: config.data.highlight.borderColor,
            });
            setSliceValueNotFound(true);
        } else {
            setSliceValueNotFound(false);
        }

        const chartType = (typeof highchartOptions.type !== 'undefined' ) ? highchartOptions.type.toLowerCase() : 'pie';

        if (!chart) {

            const chartOptions = {
                chart: {
                    type: 'pie',
                    // animation: false
                    backgroundColor: null
                },
                credits: {
                    enabled: false
                },
                // Card Attribution Used
                title: {
                    text: '',
                    margin: 0
                },
                subtitle: {
                    text: '',
                    margin: 0
                },
                lang: {
                    noData: noDataMessage
                },
                noData: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: '15px',
                        color: '#303030'
                    }
                },
                tooltip: {
                    ...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
                    borderColor: "#000000",
                    useHTML: true,
                    followPointer: true,
                    formatter: function () {
                        let formattedTooltip = "<table>"
                        formattedTooltip += formatTooltip(categoryFieldFormatters, null, t(this.point.name));
                        formattedTooltip += formatTooltip(countFieldFormatters, t(countFieldAlias), this.y);
                        formattedTooltip += (typeof this.point.value !== 'undefined') ? formatTooltip(valueFieldFormatters, t(valueFieldAlias), this.point.value) : ''; // if undefined, assume there is no Value to show
                        formattedTooltip += "</table>";
                        return formattedTooltip;
                    }
                },
                legend: {
                    itemStyle: {
                        color: '#727272',
                        fontWeight: 'bold'
                    }
                },
                plotOptions: {
                    pie: {
                        showInLegend: highchartOptions.plotOptions.pie.showInLegend ? highchartOptions.plotOptions.pie.showInLegend : false,
                        allowPointSelect: true,
                        cursor: 'pointer',
                        states: {
                            inactive: {
                                enabled: false
                            }
                        },
                        dataLabels: {
                            ...(highchartOptions.plotOptions.pie.dataLabels ? highchartOptions.plotOptions.pie.dataLabels : {}),
                            distance: 20,
                            padding: 0,
                            color: persist.themeLight ? '#727272' : '#ffffff',
                            style: {
                                textOutline: false
                            },
                            connectorColor: persist.themeLight ? '#767676' : '#ffffff',
                            formatter: function () {
                                return t(this.point.name);
                            }
                        },
                        startAngle: chartType === 'semicircle' ? -90 : null,
                        endAngle: chartType === 'semicircle' ? 90 : null,
                        center: chartType === 'semicircle' ? ["50%", "75%"] : ["50%", "50%"],
                        size: chartType === 'semicircle' ? "100%" : null,
                        animation: true,
                        point: {
                            events: {
                                legendItemClick: function(event){
                                    //clickEvent(event);
                                    return false;
                                }
                            }
                        }
                    },
                    series: {
                        // animation: false,
                        cursor: 'pointer',
                        events: {
                            click: function (event) {
                                clickEvent(event);
                                return false;
                            }
                        }
                    }
                },
                series: [{
                    name: '{0}'.format(categoryField),
                    data: seriesData,
                    innerSize: (chartType === 'donut' || chartType === 'semicircle') ? '40%' : null
                }],
                responsive: {
                    rules: [{
                        condition: {
                            maxWidth: 500
                        },
                        chartOptions: {
                            legend: {
                                align: 'center',
                                verticalAlign: 'bottom',
                                layout: 'horizontal'
                            }
                        }
                    }]
                }
            };

            // console.log('PieChart: Create');

            setChart(Highcharts.chart(chartRef.current, chartOptions));
        } else {

            // console.log('PieChart: Update');

            chart.update({
                // NOTE: We need to update our handlers otherwise they will point to
                // a previously defined function with a context that does not match
                // the current state - to reproduce comment out plot options
                // filter once chart, clear, then filter the 2nd chart and all
                // previously applied filters will be displayed
                tooltip: {
                    ...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
                    borderColor: "#000000",
                    useHTML: true,
                    followPointer: true,
                    formatter: function () {
                        let formattedTooltip = "<table>"
                        formattedTooltip += formatTooltip(categoryFieldFormatters, null, t(this.point.name));
                        formattedTooltip += formatTooltip(countFieldFormatters, t(countFieldAlias), this.y);
                        formattedTooltip += (typeof this.point.value !== 'undefined') ? formatTooltip(valueFieldFormatters, t(valueFieldAlias), this.point.value) : ''; // if undefined, assume there is no Value to show
                        formattedTooltip += "</table>";
                        return formattedTooltip;
                    },
                    // valueDecimals: 2
                },
                plotOptions: {
                    pie: {
                        showInLegend: highchartOptions.plotOptions.pie.showInLegend ? highchartOptions.plotOptions.pie.showInLegend : false,
                        allowPointSelect: true,
                        cursor: 'pointer',
                        states: {
                            inactive: {
                                enabled: false
                            }
                        },
                        dataLabels: {
                            ...(highchartOptions.plotOptions.pie.dataLabels ? highchartOptions.plotOptions.pie.dataLabels : {}),
                            distance: 20,
                            padding: 0,
                            color: persist.themeLight ? '#727272' : '#ffffff',
                            style: {
                                textOutline: false
                            },
                            connectorColor: persist.themeLight ? '#767676' : '#ffffff',
                            formatter: function () {
                                return t(this.point.name);
                            }
                        },
                        startAngle: chartType === 'semicircle' ? -90 : null,
                        endAngle: chartType === 'semicircle' ? 90 : null,
                        center: chartType === 'semicircle' ? ["50%", "100%"] : ["50%", "50%"],
                        size: chartType === 'semicircle' ? "120%" : null,
                        animation: true,
                        point: {
                            events: {
                                legendItemClick: function(event){
                                    //clickEvent(event);
                                    return false;
                                }
                            }
                        }
                    },
                    series: {
                        cursor: 'pointer',
                        events: {
                            click: function (event) {
                                clickEvent(event);
                                return false;
                            }
                        }
                    }
                },
                series: [{
                    name: '{0}'.format(categoryField),
                    data: seriesData,
                    innerSize: (chartType === 'donut' || chartType === 'semicircle') ? '40%' : null
                }]
            }, true, true);

        }
    };

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '0px'}
		);

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }
            <Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, sliceValueNotFound)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}>
                </CardHeader>
                <CardContent style={cardContentStyle}>
                    <div id={"pccomp"} ref={chartRef} style={{position: 'relative', top: '0px', width: '100%', height: '100%'}}></div>
                </CardContent>
            </Card>
        </Root>
    );
}
